import { create } from 'zustand'
import {
  getOrganizationSettings,
  updateOrgProfile,
  updateWritingPreferences,
} from '@/lib/api'
import type { OrgSettings, OrgProfile, WritingPreferences } from '@/lib/schemas'
import { useOnboardingStore } from '@/stores/onboarding-store'

// Re-export types for convenience
export type { OrgSettings, OrgProfile, WritingPreferences }

// ===================
// Store Definition
// ===================

interface SettingsState {
  // State
  organization: OrgSettings | null
  isLoading: boolean
  isSaving: boolean
  error: string | null

  // Actions
  loadSettings: () => Promise<void>
  saveProfile: (profile: Partial<OrgProfile>) => Promise<boolean>
  saveWritingPreferences: (prefs: Partial<WritingPreferences>) => Promise<boolean>
  clearError: () => void
}

export const useSettingsStore = create<SettingsState>()((set) => ({
  // Initial state
  organization: null,
  isLoading: false,
  isSaving: false,
  error: null,

  loadSettings: async () => {
    try {
      set({ isLoading: true, error: null })
      const { organization } = await getOrganizationSettings()
      set({ organization, isLoading: false })
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load settings',
      })
    }
  },

  saveProfile: async (profile) => {
    try {
      set({ isSaving: true, error: null })
      const { organization } = await updateOrgProfile(profile)
      set({ organization, isSaving: false })
      // Refresh onboarding status so checklist reflects profile completion
      void useOnboardingStore.getState().fetchStatus()
      return true
    } catch (err) {
      set({
        isSaving: false,
        error: err instanceof Error ? err.message : 'Failed to save profile',
      })
      return false
    }
  },

  saveWritingPreferences: async (prefs) => {
    try {
      set({ isSaving: true, error: null })
      const { organization } = await updateWritingPreferences(prefs)
      set({ organization, isSaving: false })
      // Refresh onboarding status so checklist reflects profile completion
      void useOnboardingStore.getState().fetchStatus()
      return true
    } catch (err) {
      set({
        isSaving: false,
        error: err instanceof Error ? err.message : 'Failed to save preferences',
      })
      return false
    }
  },

  clearError: () => set({ error: null }),
}))
